import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Input, Label, Spinner } from "reactstrap";

import BeforeAfterSlider from "./BeforeAfterSlider";
import {
  ClientRecord,
  ClientRecordAttachment,
  downloadAttachmentBlob,
  getRecord,
} from "../../services/clientRecordService";

type EvolutionSessionsCompareProps = {
  evolutions: ClientRecord[];
};

type PreviewMap = Record<number, string>;

const pickReady = (
  attachments: ClientRecordAttachment[],
  type: "before" | "after"
): ClientRecordAttachment | null =>
  attachments.find((item) => item.attachment_type === type && item.status === "ready") ??
  null;

const EvolutionSessionsCompare = ({ evolutions }: EvolutionSessionsCompareProps) => {
  const { t } = useTranslation();
  const sorted = useMemo(
    () =>
      [...evolutions].sort((a, b) =>
        String(b.created_at).localeCompare(String(a.created_at))
      ),
    [evolutions]
  );

  const [leftId, setLeftId] = useState<string>("");
  const [rightId, setRightId] = useState<string>("");
  const [leftAttachments, setLeftAttachments] = useState<ClientRecordAttachment[]>([]);
  const [rightAttachments, setRightAttachments] = useState<ClientRecordAttachment[]>([]);
  const [previews, setPreviews] = useState<PreviewMap>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sorted.length >= 2 && leftId === "" && rightId === "") {
      setLeftId(String(sorted[1].id));
      setRightId(String(sorted[0].id));
    }
  }, [sorted, leftId, rightId]);

  useEffect(() => {
    let cancelled = false;
    const urlsToRevoke: string[] = [];

    const load = async () => {
      const ids = [leftId, rightId]
        .map((value) => Number(value))
        .filter((id) => Number.isFinite(id) && id > 0);

      if (ids.length === 0) {
        setLeftAttachments([]);
        setRightAttachments([]);
        setPreviews({});
        return;
      }

      setLoading(true);

      try {
        const uniqueIds = Array.from(new Set(ids));
        const responses = await Promise.all(uniqueIds.map((id) => getRecord(id)));
        const byId = new Map(
          responses.map((response) => [response.data.id, response.data])
        );

        const left = leftId !== "" ? byId.get(Number(leftId)) : undefined;
        const right = rightId !== "" ? byId.get(Number(rightId)) : undefined;

        if (cancelled) {
          return;
        }

        setLeftAttachments(left?.attachments ?? []);
        setRightAttachments(right?.attachments ?? []);

        const all = [
          ...(left?.attachments ?? []),
          ...(right?.attachments ?? []),
        ].filter((item) => item.status === "ready");

        const nextPreviews: PreviewMap = {};
        await Promise.all(
          all.map(async (attachment) => {
            try {
              const blob = await downloadAttachmentBlob(
                attachment.client_record_id,
                attachment.id
              );
              const url = URL.createObjectURL(blob);
              urlsToRevoke.push(url);
              nextPreviews[attachment.id] = url;
            } catch {
              // skip failed preview
            }
          })
        );

        if (!cancelled) {
          setPreviews(nextPreviews);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      urlsToRevoke.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [leftId, rightId]);

  if (sorted.length < 2) {
    return (
      <Alert color="light" className="border mb-3">
        {t("clients.evolution.compareSessions.needTwo")}
      </Alert>
    );
  }

  const leftBefore = pickReady(leftAttachments, "before");
  const leftAfter = pickReady(leftAttachments, "after");
  const rightBefore = pickReady(rightAttachments, "before");
  const rightAfter = pickReady(rightAttachments, "after");

  const crossLeft = leftAfter ?? leftBefore;
  const crossRight = rightAfter ?? rightBefore;
  const canCrossSlider =
    crossLeft &&
    crossRight &&
    previews[crossLeft.id] &&
    previews[crossRight.id] &&
    crossLeft.id !== crossRight.id;

  const renderSession = (
    label: string,
    before: ClientRecordAttachment | null,
    after: ClientRecordAttachment | null
  ) => {
    if (before && after && previews[before.id] && previews[after.id]) {
      return (
        <BeforeAfterSlider
          beforeSrc={previews[before.id]}
          afterSrc={previews[after.id]}
          maxHeight={180}
        />
      );
    }

    const single = after ?? before;
    if (single && previews[single.id]) {
      return (
        <img
          src={previews[single.id]}
          alt={label}
          className="rounded w-100"
          style={{ maxHeight: 180, objectFit: "cover" }}
        />
      );
    }

    return (
      <p className="small text-muted mb-0">
        {t("clients.evolution.compareSessions.noPhotos")}
      </p>
    );
  };

  const formatOption = (record: ClientRecord): string => {
    const date = record.created_at
      ? new Date(record.created_at).toLocaleDateString()
      : `#${record.id}`;
    return t("clients.evolution.compareSessions.option", {
      date,
      id: record.id,
    });
  };

  return (
    <div className="mb-3 p-2 border rounded bg-light">
      <p className="fw-medium small mb-2">
        {t("clients.evolution.compareSessions.title")}
      </p>
      <p className="small text-muted mb-2">
        {t("clients.evolution.compareSessions.hint")}
      </p>
      <div className="row g-2 mb-2">
        <div className="col-md-6">
          <Label className="small" htmlFor="evolution-compare-left">
            {t("clients.evolution.compareSessions.sessionA")}
          </Label>
          <Input
            id="evolution-compare-left"
            type="select"
            value={leftId}
            onChange={(event) => setLeftId(event.target.value)}
          >
            <option value="">{t("clients.evolution.compareSessions.pick")}</option>
            {sorted.map((record) => (
              <option key={record.id} value={record.id}>
                {formatOption(record)}
              </option>
            ))}
          </Input>
        </div>
        <div className="col-md-6">
          <Label className="small" htmlFor="evolution-compare-right">
            {t("clients.evolution.compareSessions.sessionB")}
          </Label>
          <Input
            id="evolution-compare-right"
            type="select"
            value={rightId}
            onChange={(event) => setRightId(event.target.value)}
          >
            <option value="">{t("clients.evolution.compareSessions.pick")}</option>
            {sorted.map((record) => (
              <option key={record.id} value={record.id}>
                {formatOption(record)}
              </option>
            ))}
          </Input>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-3">
          <Spinner size="sm" />
        </div>
      ) : (
        <>
          {canCrossSlider ? (
            <div className="mb-3">
              <p className="small text-muted mb-1">
                {t("clients.evolution.compareSessions.crossSlider")}
              </p>
              <BeforeAfterSlider
                beforeSrc={previews[crossLeft!.id]}
                afterSrc={previews[crossRight!.id]}
                beforeLabel={t("clients.evolution.compareSessions.sessionA")}
                afterLabel={t("clients.evolution.compareSessions.sessionB")}
                maxHeight={200}
              />
            </div>
          ) : null}
          <div className="row g-2">
            <div className="col-md-6">
              <p className="small fw-medium mb-1">
                {t("clients.evolution.compareSessions.sessionA")}
              </p>
              {renderSession(
                t("clients.evolution.compareSessions.sessionA"),
                leftBefore,
                leftAfter
              )}
            </div>
            <div className="col-md-6">
              <p className="small fw-medium mb-1">
                {t("clients.evolution.compareSessions.sessionB")}
              </p>
              {renderSession(
                t("clients.evolution.compareSessions.sessionB"),
                rightBefore,
                rightAfter
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EvolutionSessionsCompare;
